import React, { useState, useMemo, useEffect } from "react";
import { Loader2, X, ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Square } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compliance Check Pando genérico para Licitaciones (pliego ↔ propuesta).
 * Llama a POST /api/compliance/check con pliego_file_id + propuesta_file_id.
 */
export default function LicitacionComplianceModal({ open, onClose, lic, onGenerated }) {
  const [pliegoFileId, setPliegoFileId] = useState(null);
  const [propuestaFileId, setPropuestaFileId] = useState(null);
  const [focus, setFocus] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const textFiles = useMemo(() => {
    if (!lic?.archivos) return [];
    return lic.archivos.filter(f => (f.texto_extraido || "").length > 200);
  }, [lic?.archivos]);

  const detectKind = (name = "") => {
    const n = name.toLowerCase();
    if (/(ppt|pcap|pliego|prescripciones|administrativ)/.test(n)) return "pliego";
    if (/(propuesta|oferta|memoria|t[eé]cnica)/.test(n)) return "propuesta";
    return null;
  };

  useEffect(() => {
    if (!open) return;
    let pl = null, pr = null;
    for (const f of textFiles) {
      const k = detectKind(f.nombre);
      if (k === "pliego" && !pl) pl = f.id;
      if (k === "propuesta" && !pr) pr = f.id;
    }
    setPliegoFileId(pl);
    setPropuestaFileId(pr);
    setResult(null);
    setFocus("");
  }, [open, textFiles]);

  if (!open) return null;

  const runCheck = async () => {
    if (!pliegoFileId || !propuestaFileId) return toast.error("Selecciona pliego y propuesta");
    if (pliegoFileId === propuestaFileId) return toast.error("Pliego y propuesta deben ser archivos distintos");
    setRunning(true);
    setResult(null);
    try {
      // ASYNC: HTTP 202 → polling
      const r = await axios.post(`${API}/compliance/check`, {
        lic_id: lic.id,
        pliego_file_id: pliegoFileId,
        propuesta_file_id: propuestaFileId,
        focus: focus.trim() || undefined,
        persist_as_archivo: true,
      });
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("Backend no devolvió job_id");
      const POLL_MS = 4000;
      const MAX_MS = 15 * 60 * 1000;
      const t0 = Date.now();
      let out = null;
      while (Date.now() - t0 < MAX_MS) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(res => setTimeout(res, POLL_MS));
        // eslint-disable-next-line no-await-in-loop
        const s = await axios.get(`${API}/ai-jobs/${jobId}`);
        const st = s.data?.status;
        if (st === "done") { out = s.data.result; break; }
        if (st === "failed" || st === "cancelled" || st === "not_found") {
          throw new Error(s.data?.error || `Job ${st}`);
        }
      }
      if (!out) throw new Error("Timeout esperando el job (15 min)");
      setResult(out);
      toast.success(`Compliance ${out.veredicto} · score ${out.score_global}/100 · $${out.cost_usd}`);
      onGenerated && onGenerated(out.file_id);
    } catch (e) {
      toast.error("Error compliance: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const verdictColor = result?.veredicto === "cumple" ? "emerald"
    : result?.veredicto === "cumple_con_observaciones" ? "amber" : "rose";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="lic-compliance-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-emerald-50 to-sky-50 border-b border-emerald-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="text-emerald-600" size={20} />
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Compliance Check Pando · Pliego ↔ Propuesta</h3>
              <p className="text-[11px] text-gray-600">{lic?.titulo}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white rounded" data-testid="lic-compliance-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {textFiles.length < 2 ? (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-[12px] text-amber-900">
              <AlertTriangle className="inline mr-1.5" size={14} />
              Esta licitación necesita al menos <b>2 archivos con texto extraído</b> (un pliego y una propuesta) para ejecutar un compliance check. Sube los archivos en la pestaña Documentos y vuelve aquí.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FileSelector
                  label="📋 PLIEGO (requisitos)"
                  files={textFiles}
                  selected={pliegoFileId}
                  onChange={setPliegoFileId}
                  testid="lic-compliance-pliego-select"
                />
                <FileSelector
                  label="📝 PROPUESTA (respuesta)"
                  files={textFiles}
                  selected={propuestaFileId}
                  onChange={setPropuestaFileId}
                  testid="lic-compliance-propuesta-select"
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-gray-700 block mb-1">Foco temático (opcional)</label>
                <input
                  type="text"
                  value={focus}
                  onChange={e => setFocus(e.target.value)}
                  placeholder="ej. RGPD, encaje técnico, riesgos legales…"
                  data-testid="lic-compliance-focus"
                  className="w-full text-[12px] border border-gray-300 rounded px-2 py-1.5"
                />
              </div>

              <button
                onClick={runCheck}
                disabled={running || !pliegoFileId || !propuestaFileId}
                data-testid="lic-compliance-run"
                className="w-full bg-gradient-to-r from-emerald-600 to-sky-600 hover:from-emerald-700 hover:to-sky-700 text-white font-bold text-sm py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {running ? (
                  <><Loader2 className="animate-spin" size={16} /> Analizando con Claude Sonnet 4.6…</>
                ) : (
                  <><ShieldCheck size={16} /> Ejecutar Compliance Check (~$0.10-0.30)</>
                )}
              </button>

              {result && (
                <div className="mt-3 space-y-3" data-testid="lic-compliance-result">
                  <div className={`bg-${verdictColor}-50 border-2 border-${verdictColor}-300 rounded-lg p-4`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <div className={`text-3xl font-black text-${verdictColor}-700`}>{result.score_global}</div>
                        <div>
                          <div className={`text-[11px] uppercase font-bold text-${verdictColor}-800 tracking-wide`}>
                            Veredicto: {result.veredicto.replace(/_/g, " ")}
                          </div>
                          <div className="text-[12px] text-gray-800">{result.resumen_ejecutivo}</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-500">
                        ${result.cost_usd} · {result.input_tokens.toLocaleString("es-ES")} tok IN · {result.output_tokens.toLocaleString("es-ES")} tok OUT
                      </div>
                    </div>
                    {result.estadisticas && (
                      <div className="flex gap-3 mt-3 text-[11px] flex-wrap">
                        <StatBox icon={<CheckCircle2 size={12} />} label="Cumple" n={result.estadisticas.cumple_si} color="emerald" />
                        <StatBox icon={<AlertTriangle size={12} />} label="Parcial" n={result.estadisticas.cumple_parcial} color="amber" />
                        <StatBox icon={<XCircle size={12} />} label="No cumple" n={result.estadisticas.cumple_no} color="rose" />
                        <StatBox icon={<Square size={12} />} label="No eval." n={result.estadisticas.no_evaluable} color="gray" />
                      </div>
                    )}
                  </div>

                  {result.bloqueantes?.length > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                      <div className="text-[11px] font-bold text-rose-800 mb-1">🚨 Bloqueantes ({result.bloqueantes.length})</div>
                      <ul className="text-[11px] text-rose-900 space-y-0.5 list-disc list-inside">
                        {result.bloqueantes.map((b, i) => <li key={`bloq-${i}`}>{b}</li>)}
                      </ul>
                    </div>
                  )}

                  {result.recomendaciones?.length > 0 && (
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                      <div className="text-[11px] font-bold text-sky-800 mb-1">💡 Recomendaciones</div>
                      <ul className="text-[11px] text-sky-900 space-y-0.5 list-disc list-inside">
                        {result.recomendaciones.slice(0, 6).map((r, i) => <li key={`rec-${i}`}>{r}</li>)}
                      </ul>
                    </div>
                  )}

                  {result.requisitos?.length > 0 && (
                    <details className="bg-gray-50 border border-gray-200 rounded-lg" data-testid="lic-compliance-requisitos" open>
                      <summary className="text-[11px] font-bold text-gray-700 px-3 py-2 cursor-pointer">
                        📋 Matriz de requisitos ({result.requisitos.length})
                      </summary>
                      <div className="max-h-80 overflow-y-auto px-3 pb-2">
                        <table className="w-full text-[10px]">
                          <thead className="sticky top-0 bg-gray-50">
                            <tr className="text-left text-gray-600">
                              <th className="py-1 pr-1">ID</th><th className="pr-1">Requisito</th><th className="pr-1">Crit.</th><th className="pr-1">Cumple</th><th>Gap</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.requisitos.map(r => (
                              <tr key={r.id} className="border-t border-gray-200">
                                <td className="py-1 pr-1 font-mono text-gray-500 align-top">{r.id}</td>
                                <td className="py-1 pr-1 max-w-xs align-top">
                                  <span className="text-gray-900">{r.descripcion}</span>
                                  {r.cita_pliego && (
                                    <div className="mt-0.5 text-[9px] text-gray-500 italic border-l-2 border-gray-300 pl-1.5" data-testid={`lic-compliance-cita-${r.id}`}>
                                      “{r.cita_pliego}”
                                      {r.ubicacion && <span className="not-italic font-semibold text-gray-400"> · {r.ubicacion}</span>}
                                    </div>
                                  )}
                                </td>
                                <td className="py-1 pr-1 align-top">{r.criticidad}</td>
                                <td className="py-1 pr-1 align-top">
                                  {r.cumple === "si" ? "✅" : r.cumple === "parcial" ? "⚠️" : r.cumple === "no" ? "❌" : "◻️"}
                                </td>
                                <td className="py-1 text-rose-700 max-w-xs align-top">{r.gap}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}

                  {result.file_id && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-[11px] text-emerald-800">
                      ✅ Archivo persistido en <b>Documentos</b> → categoría &quot;Compliance Pando&quot;.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FileSelector({ label, files, selected, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-700 block mb-1">{label}</label>
      <select
        value={selected || ""}
        onChange={e => onChange(e.target.value || null)}
        data-testid={testid}
        className="w-full text-[11px] border border-gray-300 rounded px-2 py-1.5 bg-white">
        <option value="">— elige un archivo —</option>
        {files.map(f => (
          <option key={f.id} value={f.id}>
            {f.nombre} ({(f.texto_extraido || "").length.toLocaleString("es-ES")} chars)
          </option>
        ))}
      </select>
    </div>
  );
}

function StatBox({ icon, label, n, color }) {
  return (
    <div className={`flex items-center gap-1 text-${color}-800`}>
      {icon}
      <span className="font-bold">{n}</span>
      <span className="opacity-70">{label}</span>
    </div>
  );
}
